/** @jsxImportSource react */
import { Typography } from '@mui/material'
import { formatValue } from '../records/fieldValues'
import { usePersonLabel } from '../records/PersonInput'
import { entityLabel } from '../../utils/helpers'
import { OP_LABELS, SYSTEM_DATES, UNARY, actionLabel, describeTiming, describeValue, todayOffset } from './automationModel'
import { useEntityFields } from './useEntityFields'

/** "When a Deal's Stage changes to Won, if Amount is at least 100,000 → Update this record, then …" */
export const useDescribeRule = (entityType) => {
  const { byKey } = useEntityFields(entityType)
  const personLabel = usePersonLabel()
  const fixed = (field, value) => (field.fieldType === 'USER' ? personLabel(value) : formatValue(field, value))
  const value = (key, v) => describeValue(byKey[key], v, byKey, fixed)
  const label = (key) => byKey[key]?.label || SYSTEM_DATES.find((f) => f.fieldKey === key)?.label || key
  const person = (r) => (r === '$actor' ? 'whoever changed it' : r?.startsWith('field:') ? `its ${label(r.slice(6))}` : personLabel(r))

  return (definition) => {
    const { trigger, conditions = [], actions = [] } = definition
    const noun = entityLabel(entityType)
    const when = {
      CREATED: `a ${noun} is created`,
      UPDATED: `a ${noun} is updated`,
      CREATED_OR_UPDATED: `a ${noun} is created or updated`,
      DELETED: `a ${noun} is deleted`,
      FIELD_CHANGED: `a ${noun}'s ${label(trigger.field)} changes${trigger.to != null ? ` to ${value(trigger.field, trigger.to)}` : ''}`,
      DATE_REACHED: `it's ${describeTiming(trigger, label)}`,
      SCHEDULE: `${describeTiming(trigger, label)}, for each ${noun}`,
    }[trigger.type]
    const ifs = conditions.map((c) => (c.field?.startsWith('$')
      ? `${label(c.field)} ${c.op === 'lt' ? 'more than' : 'within the last'} ${Math.abs(todayOffset(c.value))} days ago`
      : `${label(c.field)} ${OP_LABELS[c.op] || c.op}${UNARY.includes(c.op) ? '' : ` ${value(c.field, c.value)}`}`))
    const thens = actions.map((a) => {
      if (a.type === 'UPDATE_RECORD') {
        const sets = Object.entries(a.params?.values || {}).map(([k, v]) => `${label(k)} to ${v == null || v === '' ? 'empty' : value(k, v)}`)
        return `set ${sets.join(', ') || '…'}`
      }
      if (a.type === 'NOTIFY') {
        const who = (a.params?.recipients || []).map(person)
        return `notify ${who.join(', ') || '…'}`
      }
      if (a.type === 'CREATE_RECORD') return `create a ${a.params?.formCode || '…'} record`
      if (a.type === 'CREATE_TASK') {
        const due = a.params?.dueInDays == null ? '' : a.params.dueInDays === 0 ? ' due today' : ` due in ${a.params.dueInDays} day${a.params.dueInDays === 1 ? '' : 's'}`
        return `give ${a.params?.assignee ? person(a.params.assignee) : '…'} a task “${a.params?.title || '…'}”${due}`
      }
      if (a.type === 'SEND_EMAIL') return `email ${(a.params?.to || []).map((t) => (t.startsWith('field:') ? `its ${label(t.slice(6))}` : t)).join(', ') || '…'}`
      if (a.type === 'ASSIGN_ROUND_ROBIN') return `set ${label(a.params?.field) || '…'} to ${(a.params?.members || []).map(personLabel).join(', ') || '…'} in turn`
      return actionLabel(a.type)
    })
    const start = trigger.type === 'SCHEDULE' ? when.charAt(0).toUpperCase() + when.slice(1) : `When ${when}`
    return `${start}${ifs.length ? `, if ${ifs.join(' and ')}` : ''} → ${thens.join(', then ') || 'nothing yet'}`
  }
}

const RuleSummary = ({ rule, ...props }) => {
  const describe = useDescribeRule(rule.entityType)
  return <Typography variant="body2" color="text.secondary" {...props}>{describe(rule.definition)}</Typography>
}

export default RuleSummary
